import React, { useState, useRef, useCallback } from "react";
import {
  Box,
  Popover,
  MenuItem,
  Checkbox,
  ListItemText,
} from "@mui/material";
import {
  SearchOutlined,
  Close as CloseIcon,
} from "@mui/icons-material";
import { enumToArray } from "../../../shared/models/models";
import {
  CategoryReference,
  ManufacturerReference,
  ProductAvailabilityCode,
  StockItemGroupStats,
  StockItemsQuery,
  productAvailability,
} from "../models/models";
import { ProductConditionCode, productCondition } from "../../../common/models/CommonModels";
import { dt } from "../../../shared/themes/redesignTokens";

// ═══════════════════════════════════════════════════════════════
//  DESIGN TOKENS (subset)
// ═══════════════════════════════════════════════════════════════

// ═══════════════════════════════════════════════════════════════
//  PROPS
// ═══════════════════════════════════════════════════════════════

interface InventoryFilterBarProps {
  query: StockItemsQuery;
  groups: StockItemGroupStats[];
  manufacturers: ManufacturerReference[];
  categories: CategoryReference[];
  tInventory: (key: string, options?: any) => string;
  tCommon: (key: string, options?: any) => string;
  onChange: (query: StockItemsQuery) => void;
}

// ═══════════════════════════════════════════════════════════════
//  COMPONENT
// ═══════════════════════════════════════════════════════════════

const InventoryFilterBar = ({
  query,
  groups,
  manufacturers,
  categories,
  tInventory,
  tCommon,
  onChange,
}: InventoryFilterBarProps) => {
  const [searchFocused, setSearchFocused] = useState(false);
  const [searchValue, setSearchValue] = useState<string>(query.search || "");
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Popover anchors
  const [availAnchor, setAvailAnchor] = useState<HTMLElement | null>(null);
  const [condAnchor, setCondAnchor] = useState<HTMLElement | null>(null);
  const [brandAnchor, setBrandAnchor] = useState<HTMLElement | null>(null);
  const [catAnchor, setCatAnchor] = useState<HTMLElement | null>(null);

  // Derived
  const forSaleGroup = groups.find((g) => !g.isPrivate);
  const internalGroup = groups.find((g) => g.isPrivate);
  const allConditionCodes = enumToArray<ProductConditionCode>(ProductConditionCode);

  const hasAnyFilter =
    !!query.groupId ||
    !!query.availabilityCode ||
    !!query.manufacturerId ||
    !!query.categoryId ||
    !!query.search ||
    (query.conditionCodes && query.conditionCodes.length < allConditionCodes.length);

  // ─── Handlers ───────────────────────────────────────────────

  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value;
      setSearchValue(value);
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (value.trim().length === 0) {
        onChange({ ...query, search: undefined, pageNo: 1 });
        return;
      }
      debounceRef.current = setTimeout(() => {
        onChange({ ...query, search: value || undefined, pageNo: 1 });
      }, 400);
    },
    [query, onChange]
  );

  const handleSearchKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        if (debounceRef.current) clearTimeout(debounceRef.current);
        onChange({ ...query, search: searchValue || undefined, pageNo: 1 });
      }
    },
    [searchValue, query, onChange]
  );

  const handleGroupChange = (groupId?: string) => {
    onChange({ ...query, groupId, pageNo: 1 });
  };

  const handleAvailabilityChange = (code?: ProductAvailabilityCode) => {
    onChange({ ...query, availabilityCode: code, pageNo: 1 });
    setAvailAnchor(null);
  };

  const handleConditionToggle = (code: ProductConditionCode) => {
    const current = query.conditionCodes || allConditionCodes;
    const isChecked = current.includes(code);
    const next = isChecked
      ? current.filter((c) => c !== code)
      : [...current, code];
    onChange({ ...query, conditionCodes: next.length > 0 ? next : undefined, pageNo: 1 });
  };

  const handleManufacturerChange = (id?: string) => {
    onChange({ ...query, manufacturerId: id, pageNo: 1 });
    setBrandAnchor(null);
  };

  const handleCategoryChange = (id?: string) => {
    onChange({ ...query, categoryId: id, pageNo: 1 });
    setCatAnchor(null);
  };

  const handleClearAll = () => {
    setSearchValue("");
    onChange({
      ...query,
      search: undefined,
      groupId: undefined,
      availabilityCode: undefined,
      conditionCodes: allConditionCodes,
      manufacturerId: undefined,
      categoryId: undefined,
      pageNo: 1,
    });
  };

  // ─── Chip style helper ──────────────────────────────────────

  const chipSx = (isActive: boolean) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    padding: "6px 14px",
    borderRadius: dt.radiusPill,
    fontSize: "12px",
    fontWeight: 600,
    fontFamily: dt.font,
    cursor: "pointer",
    whiteSpace: "nowrap" as const,
    transition: "all 0.2s ease",
    backgroundColor: isActive ? dt.blueSoft : dt.inputBg,
    color: isActive ? dt.blue : dt.textSecondary,
    border: isActive ? `1px solid ${dt.blue}30` : "1px solid transparent",
    "&:hover": {
      backgroundColor: isActive ? dt.blueGlow : "#E8EBF0",
      color: isActive ? dt.blueHover : dt.textPrimary,
    },
  });

  const segmentBtnSx = (isActive: boolean) => ({
    padding: "6px 14px",
    fontSize: "12px",
    fontWeight: 600,
    fontFamily: dt.font,
    cursor: "pointer",
    whiteSpace: "nowrap" as const,
    transition: "all 0.2s ease",
    borderRadius: dt.radiusPill,
    backgroundColor: isActive ? dt.blue : "transparent",
    color: isActive ? "#FFFFFF" : dt.textSecondary,
    border: "none",
    "&:hover": {
      backgroundColor: isActive ? dt.blueHover : dt.inputBg,
      color: isActive ? "#FFFFFF" : dt.textPrimary,
    },
  });

  // ─── Popover styling ───────────────────────────────────────

  const popoverPaperSx = {
    borderRadius: "12px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.08), 0 1px 4px rgba(0,0,0,0.04)",
    border: `1px solid ${dt.cardBorder}`,
    mt: 0.5,
    maxHeight: "300px",
    overflowY: "auto" as const,
  };

  const menuItemSx = {
    fontFamily: dt.font,
    fontSize: "13px",
    "&:hover": { backgroundColor: dt.blueSoft },
    "&.Mui-selected": { backgroundColor: dt.blueSoft, color: dt.blue },
  };

  // ─── Find labels for selected filters ──────────────────────

  const selectedManufacturerName = manufacturers.find((m) => m.id === query.manufacturerId)?.name;
  const selectedCategoryName = categories.find((c) => c.id === query.categoryId)?.name;

  const availabilityOptions: { code?: ProductAvailabilityCode; label: string }[] = [
    { code: undefined, label: tInventory("allGroups") },
    { code: ProductAvailabilityCode.inStock, label: tInventory("inStock") },
    { code: ProductAvailabilityCode.in7Days, label: tInventory("in7Days") },
    { code: ProductAvailabilityCode.in14Days, label: tInventory("in14Days") },
    { code: ProductAvailabilityCode.in1Month, label: tInventory("in1Month") },
  ];

  const conditionOptions = [
    ProductConditionCode.new,
    ProductConditionCode.refit,
    ProductConditionCode.used,
    ProductConditionCode.notApplicable,
  ];

  const currentConditions = query.conditionCodes || allConditionCodes;
  const conditionFilterActive = currentConditions.length < allConditionCodes.length;

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "10px" }}>
      {/* Row 1: Search + Group toggle */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "10px", alignItems: "center" }}>
        {/* Search */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            background: dt.inputBg,
            borderRadius: dt.radiusSm,
            border: `1.5px solid ${searchFocused ? dt.blue : "transparent"}`,
            padding: "0 12px",
            height: "36px",
            boxShadow: searchFocused ? `0 0 0 3px ${dt.blueGlow}` : "none",
            transition: "all 0.2s ease",
            flex: { xs: "1 1 100%", sm: "0 1 260px" },
            minWidth: "180px",
          }}
        >
          <SearchOutlined sx={{ fontSize: "16px", color: searchFocused ? dt.blue : dt.textMuted }} />
          <input
            style={{
              border: "none",
              outline: "none",
              width: "100%",
              fontSize: "13px",
              fontFamily: 'var(--font-sans)',
              color: dt.textPrimary,
              background: "transparent",
              marginLeft: "8px",
            }}
            placeholder={tInventory("searchByNumber")}
            value={searchValue}
            onChange={handleSearchChange}
            onKeyDown={handleSearchKeyDown}
            onFocus={() => setSearchFocused(true)}
            onBlur={() => setSearchFocused(false)}
          />
          {searchValue && (
            <CloseIcon
              onClick={() => { setSearchValue(""); onChange({ ...query, search: undefined, pageNo: 1 }); }}
              sx={{ fontSize: "16px", color: dt.textMuted, cursor: "pointer", "&:hover": { color: dt.textPrimary } }}
            />
          )}
        </Box>

        {/* Group segmented toggle */}
        <Box sx={{
          display: "inline-flex",
          alignItems: "center",
          backgroundColor: dt.inputBg,
          borderRadius: dt.radiusPill,
          padding: "3px",
          gap: "2px",
        }}>
          <Box component="span" onClick={() => handleGroupChange(undefined)} sx={segmentBtnSx(!query.groupId)}>
            {tInventory("allGroups")}
          </Box>
          {forSaleGroup && (
            <Box component="span" onClick={() => handleGroupChange(forSaleGroup.id)} sx={segmentBtnSx(query.groupId === forSaleGroup.id)}>
              {tInventory("forSale")}
            </Box>
          )}
          {internalGroup && (
            <Box component="span" onClick={() => handleGroupChange(internalGroup.id)} sx={segmentBtnSx(query.groupId === internalGroup.id)}>
              {tInventory("internalStock")}
            </Box>
          )}
        </Box>
      </Box>

      {/* Row 2: Filter chips */}
      <Box sx={{ display: "flex", flexWrap: "wrap", gap: "8px", alignItems: "center" }}>
        {/* Availability chip */}
        <Box
          onClick={(e) => setAvailAnchor(e.currentTarget)}
          sx={chipSx(!!query.availabilityCode)}
        >
          {tInventory("availability")}
          {query.availabilityCode != null && `: ${productAvailability(query.availabilityCode, tInventory)}`}
        </Box>
        <Popover
          open={Boolean(availAnchor)}
          anchorEl={availAnchor}
          onClose={() => setAvailAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          slotProps={{ paper: { sx: popoverPaperSx } }}
        >
          {availabilityOptions.map((opt) => (
            <MenuItem
              key={String(opt.code ?? "all")}
              selected={query.availabilityCode === opt.code}
              onClick={() => handleAvailabilityChange(opt.code)}
              sx={menuItemSx}
            >
              {opt.label}
            </MenuItem>
          ))}
        </Popover>

        {/* Condition chip (multi-select) */}
        <Box
          onClick={(e) => setCondAnchor(e.currentTarget)}
          sx={chipSx(conditionFilterActive)}
        >
          {tInventory("condition")}
          {conditionFilterActive && ` (${currentConditions.length})`}
        </Box>
        <Popover
          open={Boolean(condAnchor)}
          anchorEl={condAnchor}
          onClose={() => setCondAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          slotProps={{ paper: { sx: popoverPaperSx } }}
        >
          {conditionOptions.map((code) => (
            <MenuItem
              key={code}
              onClick={() => handleConditionToggle(code)}
              sx={{ ...menuItemSx, gap: "4px" }}
            >
              <Checkbox
                checked={currentConditions.includes(code)}
                size="small"
                sx={{ padding: "2px", color: dt.textMuted, "&.Mui-checked": { color: dt.blue } }}
              />
              <ListItemText
                primary={tCommon(`productCondition.${productCondition(code)}`)}
                primaryTypographyProps={{ fontSize: "13px", fontFamily: dt.font }}
              />
            </MenuItem>
          ))}
        </Popover>

        {/* Brand chip */}
        <Box
          onClick={(e) => setBrandAnchor(e.currentTarget)}
          sx={chipSx(!!query.manufacturerId)}
        >
          {tInventory("brand")}
          {selectedManufacturerName && `: ${selectedManufacturerName}`}
        </Box>
        <Popover
          open={Boolean(brandAnchor)}
          anchorEl={brandAnchor}
          onClose={() => setBrandAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          slotProps={{ paper: { sx: popoverPaperSx } }}
        >
          <MenuItem
            onClick={() => handleManufacturerChange(undefined)}
            selected={!query.manufacturerId}
            sx={menuItemSx}
          >
            {tInventory("allGroups")}
          </MenuItem>
          {manufacturers.map((m) => (
            <MenuItem
              key={m.id}
              selected={query.manufacturerId === m.id}
              onClick={() => handleManufacturerChange(m.id)}
              sx={menuItemSx}
            >
              {m.name}
            </MenuItem>
          ))}
        </Popover>

        {/* Category chip */}
        <Box
          onClick={(e) => setCatAnchor(e.currentTarget)}
          sx={chipSx(!!query.categoryId)}
        >
          {tInventory("category")}
          {selectedCategoryName && `: ${selectedCategoryName}`}
        </Box>
        <Popover
          open={Boolean(catAnchor)}
          anchorEl={catAnchor}
          onClose={() => setCatAnchor(null)}
          anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
          slotProps={{ paper: { sx: popoverPaperSx } }}
        >
          <MenuItem
            onClick={() => handleCategoryChange(undefined)}
            selected={!query.categoryId}
            sx={menuItemSx}
          >
            {tInventory("allGroups")}
          </MenuItem>
          {categories.map((c) => (
            <MenuItem
              key={c.id}
              selected={query.categoryId === c.id}
              onClick={() => handleCategoryChange(c.id)}
              sx={menuItemSx}
            >
              {c.name}
            </MenuItem>
          ))}
        </Popover>

        {/* Clear all */}
        {hasAnyFilter && (
          <Box
            onClick={handleClearAll}
            sx={{
              fontSize: "12px",
              fontWeight: 600,
              fontFamily: dt.font,
              color: dt.blue,
              cursor: "pointer",
              padding: "6px 10px",
              borderRadius: dt.radiusPill,
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: dt.blueSoft,
                color: dt.blueHover,
              },
            }}
          >
            {tInventory("clearFilters")}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default InventoryFilterBar;
